import produce from 'immer';
import { intersection } from 'lodash/fp';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selector } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  stepWorkflowVersionTargets,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  getAllWorkflowActionSteps,
  workflowNextStepNames,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import {
  generateInquiryFlowStatePropertyUsableObjectsFromSteps,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowStepAction, WorkflowVersion } from 'dashboard/models/workflow-version';
import { RootState } from 'dashboard/redux/store';
import { convertFieldSchemasToJsonSchema, FieldSchema, FieldSchemaType } from 'lib/fields';
import {
  isUiFormFieldComponentConfig,
  UiComponentConfig,
  UiFormFieldComponentConfig,
} from 'lib/interfaces/ui-components';
import { JsonSchema, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

export type ServerLogicJsonSchema = {
  type: JsonSchemaTypes.Object;
  properties: {
    field_values: JsonSchema;
    flow_state: JsonSchema;
  };
};

export type ClientLogicJsonSchema = {
  type: JsonSchemaTypes.Object;
  properties: {
    form: JsonSchema;
    meta?: JsonSchema;
  };
};

export type ComponentLogicJsonSchema = {
  server: ServerLogicJsonSchema;
  client: ClientLogicJsonSchema;
};

/**
 * client-side meta is only available to non-form field components.
 * currently this object only contains a boolean field 'polling.'
 */
export const metaJsonSchema: JsonSchema = {
  type: JsonSchemaTypes.Object,
  properties: {
    polling: {
      type: JsonSchemaTypes.Boolean,
      fieldType: FieldSchemaType.Boolean,
      description: 'Whether or not the UI is waiting for an API request to complete to proceed.',
    },
  },
};

const convertFormFieldComponentsToJsonSchema = ({
  formFieldComponentConfigs,
  fieldsJsonSchema,
}: {
  formFieldComponentConfigs: UiFormFieldComponentConfig[];
  fieldsJsonSchema: JsonSchema;
}): JsonSchema => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const properties: JsonSchemaObject['properties'] = formFieldComponentConfigs.reduce(
    (properties: JsonSchema, { name, config: { field } }) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      fieldsJsonSchema.properties?.[field] != null
        ? {
            ...properties,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            [name]: fieldsJsonSchema.properties?.[field],
          }
        : properties,
    {},
  );
  return {
    type: JsonSchemaTypes.Object,
    properties,
  };
};

const getUsableWorkflowStepsRecursive = ({
  stepNames,
  steps,
  workflowVersionMap,
}: {
  stepNames: string[];
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
}): WorkflowStepAction[] => {
  if (stepNames.length === 0) return [];
  const { workflowVersionIds, actionSteps } = Object.values(workflowVersionMap).reduce(
    (
      acc: { workflowVersionIds: string[]; actionSteps: WorkflowStepAction[] },
      wfv: WorkflowVersion,
    ) => {
      const allActionSteps = getAllWorkflowActionSteps(wfv.attributes['steps-config']);
      return produce(acc, (draftAcc) => {
        if (intersection(stepNames, workflowNextStepNames(allActionSteps)).length > 0) {
          draftAcc.workflowVersionIds.push(wfv.id);
          draftAcc.actionSteps.push(...allActionSteps);
        }
      });
    },
    { workflowVersionIds: [], actionSteps: [] },
  );
  const nextTargetStepNames = steps
    .filter((step) => intersection(workflowVersionIds, stepWorkflowVersionTargets(step)).length > 0)
    .map((step) => step.name);
  const nextWorkflowVersionMap = Object.entries(workflowVersionMap).reduce(
    (acc, [id, wfv]) => (workflowVersionIds.includes(id) ? acc : { ...acc, [id]: wfv }),
    {},
  );
  return [
    ...actionSteps,
    ...getUsableWorkflowStepsRecursive({
      stepNames: nextTargetStepNames,
      steps,
      workflowVersionMap: nextWorkflowVersionMap,
    }),
  ];
};

const generateFlowStateJsonSchema = ({
  currentStep,
  steps,
  workflowVersionMap,
}: {
  currentStep: InquiryTemplateVersionStepUi;
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
}): JsonSchema => {
  const usableWorkflowSteps: WorkflowStepAction[] = getUsableWorkflowStepsRecursive({
    stepNames: [currentStep.name],
    steps,
    workflowVersionMap,
  });
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const properties: JsonSchemaObject['properties'] =
    generateInquiryFlowStatePropertyUsableObjectsFromSteps(usableWorkflowSteps).reduce(
      (properties: JsonSchema, config: UsableObjectConfig) => ({
        ...properties,
        [config.token as string]: {
          label: config.label,
          type: config.type,
        },
      }),
      {},
    );
  return {
    type: JsonSchemaTypes.Object,
    properties,
  };
};

export const generateComponentLogicJsonSchema = ({
  currentStep,
  currentComponentConfig,
  steps,
  fieldSchema,
  workflowVersionMap,
}: {
  currentStep: InquiryTemplateVersionStepUi;
  currentComponentConfig: UiComponentConfig;
  steps: InquiryTemplateVersionStep[];
  fieldSchema: FieldSchema[];
  workflowVersionMap: WorkflowVersionMap;
}): ComponentLogicJsonSchema => {
  const fieldsJsonSchema = convertFieldSchemasToJsonSchema(fieldSchema);
  const flowStateJsonSchema = generateFlowStateJsonSchema({
    currentStep,
    steps,
    workflowVersionMap,
  });
  const formComponentsJsonSchema = convertFormFieldComponentsToJsonSchema({
    formFieldComponentConfigs: uiStepAllComponentConfigs(currentStep).filter(
      (component): component is UiFormFieldComponentConfig =>
        isUiFormFieldComponentConfig(component) && component.name !== currentComponentConfig.name,
    ),
    fieldsJsonSchema,
  });

  const componentLogicJsonSchema: ComponentLogicJsonSchema = {
    server: {
      type: JsonSchemaTypes.Object,
      properties: {
        field_values: fieldsJsonSchema,
        flow_state: flowStateJsonSchema,
      },
    },
    client: {
      type: JsonSchemaTypes.Object,
      properties: {
        form: formComponentsJsonSchema,
      },
    },
  };

  return isUiFormFieldComponentConfig(currentComponentConfig)
    ? componentLogicJsonSchema
    : produce(componentLogicJsonSchema, (draftSchema) => {
        draftSchema.client.properties['meta'] = metaJsonSchema;
      });
};

/**
 * @name `useComponentLogicJsonSchema`
 *
 * Returns JSON schema used for configuring component conditional logic
 * such as hidden, disabled, optional, and validations.
 *
 * Contains two sections:
 * - server: evaluated server-side prior to page load.
 *   - field_values: ITV field schema
 *   - flow_state  : Inquiry internal state. Holds values such as verification statuses / attempt counts
 *                  Only verification objects created in the path to the current step are in flow_state.
 * - client: evaluated client-side after page load in realtime.
 *   - form       : the values of the form field components on the current step
 *
 * References:
 * - Design doc:        https://www.notion.so/withpersona/JsonLogic-63ead3cc92bf46789ace40832f5d1a6a
 * - JSONLogic library: https://www.notion.so/withpersona/RESOURCE-JSONLogic-library-9037c887812b45fd90833e1824953fad
 */
export default (
  currentStep: InquiryTemplateVersionStepUi,
  currentComponentConfig: UiComponentConfig,
): ComponentLogicJsonSchema => {
  const steps = useSelector<RootState, InquiryTemplateVersionStep[]>(
    (state) => selector(state, 'steps') ?? [],
  );
  const fieldSchema = useSelector<RootState, FieldSchema[]>(
    (state) => selector(state, 'field-schema') ?? [],
  );
  const workflowVersionMap = useSelector<RootState, WorkflowVersionMap>(
    (state) => selector(state, 'workflowVersions') ?? [],
  );
  return useMemo(
    () =>
      generateComponentLogicJsonSchema({
        currentStep,
        currentComponentConfig,
        steps,
        fieldSchema,
        workflowVersionMap,
      }),
    [currentStep, currentComponentConfig, steps, fieldSchema, workflowVersionMap],
  );
};
